import React from 'react'
import DefaultLocationFAQs from './DefaultLocationFAQs'
import DefaultTaxonomyFAQs from './DefaultTaxonomyFAQs'
import CustomFAQs from './CustomFAQs'
import {
  isLocationPageType,
  isFacetPageType
} from '../../../domain/useCases/pageTypeCheck'
import { filterFaqsByLocation } from './filterFaqsByLocation'
import type { PageType, FAQ, LocationPart } from '@/types/models'

type Term = {
  fieldValue: string
}

type LocationAndTaxonomyAccordionFAQsProps = {
  quick_links: []
  staticHits: []
  pageType: PageType
  termData: {
    slug: string
  }
  terms?: {
    amenities: Term[]
    options: Term[]
    therapies: Term[]
  }
  searchState: {}
  searchResults: {}
  pageData: {
    slug: string
    type: string
    title: string
    locationParts: LocationPart[]
  }
  facetData: {}
  shouldDefaultRegionalize?: boolean
  centerCount: number
  staticHitsCount: number
  termTitle: string
  customTermHeader: string
  customFAQs?: FAQ[] | []
  location: string
  compact: boolean
}

const LocationAndTaxonomyAccordionFAQs: React.FC<
  LocationAndTaxonomyAccordionFAQsProps
> = ({
  quick_links,
  staticHits,
  pageType,
  termData,
  searchState,
  searchResults,
  pageData,
  facetData,
  terms = {},
  shouldDefaultRegionalize = false,
  centerCount,
  termTitle,
  customTermHeader,
  customFAQs = [],
  location,
  compact = false
}) => {
  const isLocationPage = isLocationPageType(pageType)

  const filteredFAQs = filterFaqsByLocation({
    customFAQs,
    location,
    locationParts: pageData.locationParts,
    pageType
  })

  const shouldShowCustomFAQs = filteredFAQs.length > 0
  const shouldShowDefaultFAQs = !shouldShowCustomFAQs
  const shouldShowDefaultLocationFAQs = shouldShowDefaultFAQs && isLocationPage
  const shouldShowDefaultTaxonomyFAQs = shouldShowDefaultFAQs && !isLocationPage

  const defaultFaqProps = {
    quick_links,
    staticHits,
    pageType,
    termData,
    terms,
    searchState,
    searchResults,
    pageData,
    facetData,
    shouldDefaultRegionalize,
    centerCount,
    termTitle,
    customTermHeader,
    compact
  }

  const faqContent = (
    <>
      {shouldShowCustomFAQs && (
        <CustomFAQs
          customFAQs={filteredFAQs}
          questionsAsHeading={true}
          compact={compact}
        />
      )}
      {shouldShowDefaultLocationFAQs && (
        <DefaultLocationFAQs {...defaultFaqProps} />
      )}
      {shouldShowDefaultTaxonomyFAQs && (
        <DefaultTaxonomyFAQs {...defaultFaqProps} />
      )}
    </>
  )

  if (compact) {
    return faqContent
  }

  return (
    <div className="mx-auto max-w-screen-2xl pt-2 sm:pt-10">
      <div className="mx-auto max-w-screen-xl px-4 md:px-6 lg:px-8">
        <div className="flex flex-col justify-center sm:flex-row sm:gap-[72px] md:flex-row md:gap-[68px] lg:flex-row lg:gap-[106px] xl:flex-row xl:gap-[74px]">
          <div className="flex flex-col items-start justify-center">
            <h3 className="lg:leading-16 mb-6 flex w-full flex-col gap-2 text-4xl font-normal leading-10 text-gray-1100 sm:min-w-[250px] md:min-w-[300px] lg:mb-7 lg:min-w-[370px] lg:text-5xl xl:min-w-[400px]">
              Frequently
              <br />
              Asked Questions
            </h3>
            <div className="font-figtree mb-6 w-full grow text-lg font-normal text-gray-900 sm:min-w-[250px] md:min-w-[300px] lg:min-w-[370px] xl:min-w-[400px]">
              <span>Looking for more? </span>
              <span className="font-semibold leading-7 text-gray-1100 underline sm:block md:no-underline md:hover:underline lg:inline xl:inline">
                <a href="/resources/">Visit the Resource Center</a>
              </span>
              .
            </div>
          </div>

          <div className="relative w-full md:min-w-[250px] lg:min-w-[300px] xl:min-w-[500px]">
            {faqContent}
          </div>
        </div>
      </div>
    </div>
  )
}
export default LocationAndTaxonomyAccordionFAQs
